'use client'

import { useRef, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

/** Mouse dragging complements native touch/trackpad scrolling without activating a dragged chip. */
export default function DragScrollRow({
	children,
	className,
	label
}: {
	children: ReactNode
	className?: string
	label: string
}) {
	const gesture = useRef<{ id: number; x: number; scroll: number } | null>(null)
	const dragged = useRef(false)
	return (
		<div
			role="group"
			aria-label={label}
			className={cn(
				'flex select-none items-center gap-1.5 overflow-x-auto scrollbar-none cursor-grab data-[dragging=true]:cursor-grabbing [&_button]:select-none',
				className
			)}
			onPointerDown={(event) => {
				dragged.current = false
				if (event.pointerType === 'touch' || event.button !== 0) return
				gesture.current = {
					id: event.pointerId,
					x: event.clientX,
					scroll: event.currentTarget.scrollLeft
				}
			}}
			onPointerMove={(event) => {
				if (!(event.buttons & 1)) {
					gesture.current = null
					return
				}
				const start = gesture.current
				if (!start || event.pointerId !== start.id) return
				const delta = event.clientX - start.x
				if (!dragged.current && Math.abs(delta) < 5) return
				dragged.current = true
				event.currentTarget.dataset.dragging = 'true'
				event.currentTarget.setPointerCapture(event.pointerId)
				event.currentTarget.scrollLeft = start.scroll - delta
			}}
			onPointerUp={(event) => {
				gesture.current = null
				delete event.currentTarget.dataset.dragging
				if (event.currentTarget.hasPointerCapture(event.pointerId))
					event.currentTarget.releasePointerCapture(event.pointerId)
			}}
			onLostPointerCapture={(event) => {
				gesture.current = null
				delete event.currentTarget.dataset.dragging
			}}
			onPointerCancel={(event) => {
				gesture.current = null
				dragged.current = false
				delete event.currentTarget.dataset.dragging
			}}
			onClickCapture={(event) => {
				if (dragged.current && event.detail !== 0) {
					event.preventDefault()
					event.stopPropagation()
				}
			}}
			onDragStart={(event) => event.preventDefault()}
		>
			{children}
		</div>
	)
}
